import { DateTime } from 'luxon';

/**
 * Tiers sorted ascending by trading-volume threshold. Non-mutating.
 * Generic over the tier shape so both the volume-incentive and referral
 * volume-tiered ladders (structurally identical thresholds) can use it.
 */
export const getSortedTiers = <T extends { threshold_cents: number }>(tiers: T[]): T[] =>
    [...tiers].sort((a, b) => a.threshold_cents - b.threshold_cents);

/**
 * The tier the user is currently working toward, identified by `in_progress_tier_key`.
 * Returns null when no tier is in progress (key is null — every tier granted) or the key
 * doesn't resolve to a tier.
 */
export const getInProgressTier = <T extends { key: string }>(
    tiers: T[],
    inProgressTierKey: string | null,
): T | null => {
    if (inProgressTierKey === null) return null;
    return tiers.find((tier) => tier.key === inProgressTierKey) ?? null;
};

/**
 * Whole days remaining until an ISO 8601 expiry timestamp, rounded up, floored at 0.
 * `now` is injectable for deterministic tests.
 */
export const getDaysUntilExpiry = (expiresTs: string, now: DateTime = DateTime.now()): number =>
    Math.max(0, Math.ceil(DateTime.fromISO(expiresTs).diff(now, 'days').days));

/**
 * Display copy for the offer expiry: "Offer expires today" / "in 1 day" / "in N days".
 * `now` is injectable for deterministic tests.
 */
export const getOfferExpiryCopy = (expiresTs: string, now: DateTime = DateTime.now()): string => {
    const days = getDaysUntilExpiry(expiresTs, now);
    if (days === 0) return 'Offer expires today';
    if (days === 1) return 'Offer expires in 1 day';
    return `Offer expires in ${days} days`;
};

/**
 * Compact expiry copy for the VIP banner eyebrow: "Expires today" / "in 1 day" / "in N days".
 * `now` is injectable for deterministic tests.
 */
export const getOfferExpiryShortCopy = (expiresTs: string, now: DateTime = DateTime.now()): string => {
    const days = getDaysUntilExpiry(expiresTs, now);
    if (days === 0) return 'Expires today';
    if (days === 1) return 'Expires in 1 day';
    return `Expires in ${days} days`;
};
